<template>
  <v-card elevation="0" class="department-schedule-card">
    <v-card-text>
      <div class="department-schedule-heading">
        <span><v-icon color="primary" size="20">mdi-calendar-clock-outline</v-icon></span>
        <div><h3>Department schedule</h3><p>Set the class days, standard hours, and local time zone.</p></div>
      </div>
      <div class="weekday-label">Class days</div>
      <v-row dense>
        <v-col v-for="day in dayOptions" :key="day.value" cols="6" sm="3" md="auto">
          <v-checkbox :input-value="openWeekdays" :value="day.value" :label="day.text" dense hide-details class="mt-0" @change="updateWeekdays" />
        </v-col>
      </v-row>
      <v-row class="mt-3">
        <v-col cols="12" sm="4"><v-text-field :value="schedule.startTime" type="time" outlined dense hide-details label="Class starts" @input="updateSchedule('startTime', $event)" /></v-col>
        <v-col cols="12" sm="4"><v-text-field :value="schedule.endTime" type="time" outlined dense hide-details label="Class ends" @input="updateSchedule('endTime', $event)" /></v-col>
        <v-col cols="12" sm="4"><v-autocomplete :value="schedule.timeZone" :items="timeZones" outlined dense hide-details label="Time zone" @change="updateSchedule('timeZone', $event)" /></v-col>
      </v-row>
      <v-divider class="my-5" />
      <div class="department-reminder-heading">
        <div><strong>Missing-attendance reminder</strong><p>Optionally remind assigned teachers at a specific time relative to the class schedule.</p></div>
        <v-switch :input-value="schedule.enabled" inset hide-details label="Enabled" class="mt-0" @change="updateSchedule('enabled', $event)" />
      </div>
      <v-expand-transition>
        <v-row v-if="schedule.enabled" class="mt-2" align="center">
          <v-col cols="12" sm="4"><v-text-field :value="schedule.reminderOffsetMinutes" type="number" min="0" max="240" step="5" suffix="minutes" outlined dense hide-details label="Reminder offset" @input="updateSchedule('reminderOffsetMinutes', Number($event))" /></v-col>
          <v-col cols="12" sm="4"><v-select :value="schedule.reminderDirection" :items="directionOptions" outlined dense hide-details label="Send reminder" @change="updateSchedule('reminderDirection', $event)" /></v-col>
          <v-col cols="12" sm="4"><v-select :value="schedule.reminderReference" :items="referenceOptions" outlined dense hide-details label="Relative to" @change="updateSchedule('reminderReference', $event)" /></v-col>
          <v-col cols="12"><div class="reminder-summary">Reminder time: <strong>{{ reminderSummary }}</strong></div></v-col>
          <v-col cols="12"><v-alert text color="primary" icon="mdi-information-outline" class="mb-0">Calendar entries where students do not attend suppress reminders. If no calendar exists, meeting days and active monthly billing months are used.</v-alert></v-col>
        </v-row>
      </v-expand-transition>
    </v-card-text>
  </v-card>
</template>

<script>
const defaultSchedule = () => ({ enabled: false, startTime: "17:30", endTime: "19:30", reminderOffsetMinutes: 30, reminderDirection: "after", reminderReference: "end", timeZone: "America/New_York" });

export default {
  name: "DepartmentSchedule",
  props: { value: { type: Object, required: true } },
  data() {
    return {
      dayOptions: [
        { text: "Sunday", value: 0 }, { text: "Monday", value: 1 }, { text: "Tuesday", value: 2 },
        { text: "Wednesday", value: 3 }, { text: "Thursday", value: 4 }, { text: "Friday", value: 5 }, { text: "Saturday", value: 6 },
      ],
      timeZones: ["America/New_York", "America/Chicago", "America/Denver", "America/Phoenix", "America/Los_Angeles", "America/Anchorage", "Pacific/Honolulu", "UTC"],
      directionOptions: [{ text: "Before", value: "before" }, { text: "After", value: "after" }],
      referenceOptions: [{ text: "Class starts", value: "start" }, { text: "Class ends", value: "end" }],
    };
  },
  computed: {
    openWeekdays() { return Array.isArray(this.value.openWeekdays) ? this.value.openWeekdays : [1, 2, 3, 4, 5]; },
    schedule() {
      const existing = this.value.attendanceSchedule || {};
      return Object.assign(defaultSchedule(), existing, {
        reminderOffsetMinutes: existing.reminderOffsetMinutes === undefined ? (existing.reminderDelayMinutes === undefined ? 30 : existing.reminderDelayMinutes) : existing.reminderOffsetMinutes,
      });
    },
    reminderSummary() {
      const direction = this.schedule.reminderDirection === "before" ? "before" : "after";
      const reference = this.schedule.reminderReference === "start" ? "class starts" : "class ends";
      return `${this.schedule.reminderOffsetMinutes} minutes ${direction} ${reference}`;
    },
  },
  methods: {
    updateWeekdays(days) { this.$emit("input", Object.assign({}, this.value, { openWeekdays: days || [] })); },
    updateSchedule(field, value) {
      this.$emit("input", Object.assign({}, this.value, { attendanceSchedule: Object.assign({}, this.schedule, { [field]: value }) }));
    },
  },
};
</script>

<style scoped>
.department-schedule-card { border: 1px solid #dce8dc; border-radius: 10px !important; }
.department-schedule-heading { display: flex; align-items: flex-start; gap: 12px; }
.department-schedule-heading > span { display: flex; width: 38px; height: 38px; flex: 0 0 38px; align-items: center; justify-content: center; border-radius: 10px; background: #edf7ef; }
.department-schedule-heading h3 { margin: 0; color: #26332a; font-size: 16px; }
.department-schedule-heading p, .department-reminder-heading p { margin: 3px 0 0; color: #667269; font-size: 12px; }
.weekday-label { margin: 20px 0 10px; color: #344054; font-size: 13px; font-weight: 600; }
.department-reminder-heading { display: flex; align-items: center; justify-content: space-between; gap: 18px; }
.department-reminder-heading strong { color: #26332a; font-size: 15px; }
.reminder-summary { padding: 10px 12px; border-radius: 8px; background: #f5f8f5; color: #536159; font-size: 13px; }
@media (max-width: 600px) { .department-reminder-heading { align-items: flex-start; flex-direction: column; } }
</style>
